import { useAtomValue } from "jotai";
import { stageRefAtom, undoRedoStackAtom } from "state-atoms";
import { useCallback, useEffect, useMemo } from "react";
import Konva from "konva";
import { mapColumnValueToText } from "shared/integrations/mapper";
import { useIntegrationsProvider } from "frontend/hooks/use-integrations-provider";
import { CanvasElement, TextEnabledElement } from "shared/datamodel/schemas";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { dataLayerFieldSchema } from "shared/datamodel/schemas/element-data-layer";
import { useDynamicElementActions } from "./use-dynamic-element-actions";
import { useMondayItemsInBulk } from "frontend/canvas-designer-new/integrations/hooks/use-monday-items-bulk";

/**
 * Utility for fetching integrations from monday.com
 */
function useMondayTasks(integrations: { [integrationId: string]: string[] }) {
  const { getBoardIntegrationConfig, updateColumnValue, loadItems } = useIntegrationsProvider();
  const memoizedIntegrations = useDeepEqualMemo(integrations);

  // Convert to flat array of item IDs
  const itemIds = useMemo(() => {
    const ids: string[] = [];
    for (const itemIdList of Object.values(memoizedIntegrations)) {
      ids.push(...itemIdList);
    }
    return ids;
  }, [memoizedIntegrations]);

  const { data, isLoading } = useMondayItemsInBulk(itemIds);

  // HACK: Since we use the useIntegrationsProvider hook, we need to load the items
  // here in order to fetch the configuration of the monday board, otherwise the
  // configuration will be undefined until the items are loaded from somewhere else in the app.
  useEffect(() => {
    loadItems(memoizedIntegrations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedIntegrations]);

  const configurations = useMemo(
    () =>
      Object.keys(memoizedIntegrations).reduce((acc, integrationId) => {
        const config = getBoardIntegrationConfig(integrationId);
        if (config) {
          acc[integrationId] = config;
        }
        return acc;
      }, {} as Record<string, any>),
    [getBoardIntegrationConfig, memoizedIntegrations]
  );

  const values = useMemo(() => {
    if (!data) return {};
    // Group items by integrationId for backwards compatibility
    const grouped: Record<string, Record<string, Record<string, any>>> = {};

    for (const [itemId, itemOrError] of Object.entries(data)) {
      if (!itemOrError.data) continue;
      const task = itemOrError.data;

      // Find which integrationId this item belongs to
      const integrationId = Object.keys(memoizedIntegrations).find((intId) =>
        memoizedIntegrations[intId].includes(itemId)
      );

      if (!integrationId) continue;

      grouped[integrationId] ??= {};
      grouped[integrationId][itemId] = task.columnValues.reduce(
        (acc: Record<string, any>, column: any) => {
          acc[column.id] = mapColumnValueToText(column.value, column);
          return acc;
        },
        { name: task.name }
      );
    }

    return grouped;
  }, [data, memoizedIntegrations]);

  return {
    configurations,
    values,
    updateColumnValue,
    isLoading,
  };
}

export const useDynamicIntegrations = (elementId: string, element: CanvasElement) => {
  const data = element?.dataLayer;
  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const stageRef = useAtomValue(stageRefAtom);

  const memoizedFields = useDeepEqualMemo(data?.fields);
  const memoizedIntegrations = useDeepEqualMemo(data?.integrations);
  const integrationItemIds = useMemo(
    () =>
      Object.entries(memoizedIntegrations ?? {}).reduce((acc, [integrationId, items]) => {
        acc[integrationId] = Object.keys(items);
        return acc;
      }, {} as Record<string, string[]>),
    [memoizedIntegrations]
  );

  const { configurations, values, isLoading } = useMondayTasks(integrationItemIds);
  const { addIntegrationToElement, removeIntegrationFromElement } = useDynamicElementActions();

  const getField = useCallback(
    (fieldId: string) => {
      if (!fieldId.startsWith("integration")) {
        return memoizedFields?.[fieldId]; // Read-only compat for custom fields.
      }

      const [, integrationId, _, columnId] = fieldId.split(":");
      if (!configurations[integrationId]) {
        return;
      }

      const { columns } = configurations[integrationId];
      const column = columns?.find((column: { id: string }) => column.id === columnId);
      if (column) {
        return dataLayerFieldSchema.parse(column);
      }
    },
    [configurations, memoizedFields]
  );

  const getFieldValue = useCallback(
    (key: string, integrationId?: string, itemId?: string, columnId?: string) => {
      switch (key) {
        case "elementText": {
          return (element as TextEnabledElement)?.text ?? "";
        }

        case "integration": {
          if (!integrationId || !itemId || !columnId) {
            // This should never happen unless the data is corrupted or there is a bug in the code.
            return "";
          }

          const columnValues = values[integrationId]?.[itemId];
          if (columnValues) {
            const column = columnValues[columnId];
            if (column) {
              return String(column);
            }
          }
          return "";
        }

        default: {
          return "";
        }
      }
    },
    [(element as TextEnabledElement)?.text, values]
  );

  const getDisplayedColumnIds = useCallback(() => {
    return Object.entries(data?.elementMapping ?? {})
      .map(([key, elementId]) => {
        // Nodes are not removed from elementMapping when they are deleted.
        // We account for this by attempting to find the node before we return it.
        // This is failry costly but the only other alternative is modifying the
        // onDeleteElements function which is very risky.
        const node = stageRef?.current?.findOne((node: Konva.Node) => node.attrs.id === elementId);
        if (!node || node.attrs.element.hidden) return null;

        const columnId = key.split(":")[3];
        return columnId;
      })
      .filter(Boolean);
  }, [data, stageRef]);

  const addIntegrationItem = useCallback(
    (integrationId: string, itemId: string) => {
      addIntegrationToElement(elementId, integrationId, itemId);
    },
    [addIntegrationToElement, elementId]
  );

  const removeIntegrationItem = useCallback(
    (integrationId: string, itemId: string) => {
      removeIntegrationFromElement(elementId, integrationId, itemId);
    },
    [removeIntegrationFromElement, elementId]
  );

  /**
   * Get the configuration of an integration item.
   * Read-only compat as we currently cannot edit integration configurations.
   */
  function getIntegrationItemConfiguration(integrationId: string, itemId: string) {
    return data?.integrations?.[integrationId]?.[itemId];
  }

  /**
   * Change the configuration of an integration item.
   * @deprecated Integration configuration is not currently supported.
   */
  function _changeIntegrationItemConfiguration(
    integrationId: string,
    itemId: string,
    configuration: { columnIds: string[] | "all" }
  ) {
    undoRedoStack?.patchElement(elementId, (element) => {
      if (!element.dataLayer) return;

      const integration = element.dataLayer.integrations?.[integrationId];
      if (!integration) return;

      const item = integration[itemId];
      if (!item) return;

      item.columnIds = configuration.columnIds;
    });
  }

  return {
    isLoading,
    fieldIds: data?.fieldsOrder ?? [],
    getField,
    getFieldValue,
    getIntegrationItemConfiguration,
    getDisplayedColumnIds,
    integrationsActive: Object.keys(integrationItemIds).length > 0,
    integrationItemIds,
    integrationsConfig: configurations,
    addIntegrationItem,
    removeIntegrationItem,
  };
};
